/**
 * useTaskProjects — fetch lista progetti del tenant.
 *
 * Espone:
 *  - `projects`: lista attivi (default) o archiviati;
 *  - `loading`, `error`: stati di caricamento;
 *  - `refetch`: reload esplicito (usato dopo create/archive/restore);
 *  - `setProjectsLocally`: aggiornamento ottimistico senza refetch.
 *
 * Non gestisce il detail (con colonne): per quello usare getTaskProject(id)
 * direttamente dal service. L'hook resta leggero per non duplicare stati.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getTaskProjects } from "../../services/taskProjects";
import type { TaskProject } from "../../types/tasks";

interface UseTaskProjectsArgs {
  /** Se true, lista solo archiviati. Default false (solo attivi). */
  archived?: boolean;
  /** Search testuale opzionale. */
  search?: string;
  /** Auto-fetch on mount e quando cambiano i filtri. Default true. */
  enabled?: boolean;
}

interface UseTaskProjectsResult {
  projects: TaskProject[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setProjectsLocally: (
    updater: TaskProject[] | ((prev: TaskProject[]) => TaskProject[])
  ) => void;
}

export function useTaskProjects({
  archived = false,
  search,
  enabled = true,
}: UseTaskProjectsArgs = {}): UseTaskProjectsResult {
  const [projects, setProjects] = useState<TaskProject[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const requestGen = useRef(0);

  const refetch = useCallback(async () => {
    const gen = ++requestGen.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getTaskProjects({ archived, search });
      // Protezione stale response: se una richiesta più recente è in flight,
      // ignoriamo questa. Importante quando l'utente cambia filtri rapidamente.
      if (gen !== requestGen.current) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setProjects(list);
    } catch (err: unknown) {
      if (gen !== requestGen.current) return;
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        "Impossibile caricare i progetti.";
      setError(msg);
    } finally {
      if (gen === requestGen.current) setLoading(false);
    }
  }, [archived, search]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    refetch();
  }, [refetch, enabled]);

  const setProjectsLocally = useCallback(
    (updater: TaskProject[] | ((prev: TaskProject[]) => TaskProject[])) => {
      setProjects((prev) =>
        typeof updater === "function"
          ? (updater as (p: TaskProject[]) => TaskProject[])(prev)
          : updater
      );
    },
    []
  );

  return { projects, loading, error, refetch, setProjectsLocally };
}
